import { useState, useMemo } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/shared/ui/hover-card";
import { cn } from "@/shared/lib/utils";
import { useLeadShares } from "@/entities/lead-v2";
import { useLeadLinksShares } from "@/entities/lead-link";

type BaseProfitHoverCardProps = {
  formattedEarnings: string;
};

type LeadProfitHoverCardProps = BaseProfitHoverCardProps & {
  leadId: string;
  linkId?: never;
};

type LeadLinkProfitHoverCardProps = BaseProfitHoverCardProps & {
  linkId: string;
  leadId?: never;
};

type ProfitHoverCardProps =
  | LeadProfitHoverCardProps
  | LeadLinkProfitHoverCardProps;

export const ProfitHoverCard = ({
  formattedEarnings,
  leadId,
  linkId,
}: ProfitHoverCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const noEarning = formattedEarnings === "0";

  const leadSharesData = useLeadShares(
    leadId,
    isHovered && !!leadId && !noEarning,
  );

  const linkSharesData = useLeadLinksShares({
    linkId,
    enabled: isHovered && !!linkId && !noEarning,
  });

  const shares = leadId ? leadSharesData : linkSharesData.data;
  const isLoading = leadSharesData.isLoading || linkSharesData.isLoading;

  const shareList = useMemo(() => {
    if (noEarning) return <p className="text-sm">Нет дохода</p>;

    if (isLoading) return <p className="text-sm">Загрузка...</p>;

    if (!shares) return <p className="text-sm">Нет партнеров</p>;

    return (
      <ul className="text-sm">
        {shares.data?.map((s) => {
          return "amount" in s ? (
            <li key={s.userId} className="flex justify-between">
              <span>{s.username}</span>
              <strong>{s.amount.toFixed(2)}$</strong>
            </li>
          ) : (
            <li key={s._id} className="flex justify-between">
              <span>{s.username}</span>
              <strong>{s.totalAmount.toFixed(2)}$</strong>
            </li>
          );
        })}
      </ul>
    );
  }, [shares, noEarning, isLoading]);

  return (
    <HoverCard>
      <HoverCardTrigger
        asChild
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span>{formattedEarnings}$</span>
      </HoverCardTrigger>
      <HoverCardContent className={cn("w-80", { "animate-pulse": isLoading })}>
        {shareList}
      </HoverCardContent>
    </HoverCard>
  );
};
